/**
 * PostHog setup for the SPA: initialization, credential scrubbing, and the capture helper.
 *
 * initPostHog() is called at module scope in index.tsx, before the first render, so the SDK
 * is ready before any effect runs. Letting PostHogProvider initialize from its own effect
 * meant init ran *after* its children's effects (React flushes child effects first), and the
 * SDK dropped every mount-time capture with a "not initialized" warning.
 *
 * Use capture(posthog, eventName, properties) for explicit events throughout the app.
 */

import type { CaptureResult, PostHog } from "posthog-js";
import posthog from "posthog-js";

const envPosthogKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;

/** Set when REACT_APP_PUBLIC_POSTHOG_KEY is non-empty (initPostHog() runs in index.tsx). */
export const posthogApiKey =
  typeof envPosthogKey === "string" && envPosthogKey.length > 0
    ? envPosthogKey
    : undefined;

/** True when REACT_APP_PUBLIC_POSTHOG_KEY is set, i.e. the SDK is initialized. */
export const posthogEnabled = process.env.NODE_ENV === "production" && posthogApiKey !== undefined;

const posthogHost = process.env.REACT_APP_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

/**
 * Hostnames that mark the person with `$internal_or_test_user: true`, so the project's
 * internal-user filter can key off that person property instead of a `$host` regex.
 * The SDK's own default only covers localhost/127.0.0.1; local dev serves the SPA from
 * smsp.local.strongmindstrongperformance.com.
 */
const INTERNAL_OR_TEST_HOSTNAME =
  /^(localhost|127\.0\.0\.1|.+\.local\.strongmindstrongperformance\.com)$/;

/**
 * Password-reset keys, email-verification keys and invite tokens are SPA route params, so
 * they sit in window.location and PostHog records them on the pageview, on every
 * autocapture event fired from that screen, and on the person via the $initial_* props.
 *
 * Rewrite only these known URL-valued properties. Matching on property *names* instead
 * (anything called token/key/secret) also hits `token`, which carries the project
 * api_key — and capture answers 200 {"status":"Ok"} for a token it cannot resolve, so
 * that silently drops every event with no failed request to notice.
 *
 * Hash fragments need no handling here: disable_capture_url_hashes is on via defaults.
 */
const SECRET_PATH_SEGMENT =
  /\/(account\/password\/reset\/key|account\/verify-email|invite)\/[^/?#]+/g;

const URL_PROPERTIES = [
  "$current_url",
  "$pathname",
  "$referrer",
  "$initial_current_url",
  "$initial_pathname",
  "$initial_referrer"
];

function redactUrlProperties<T extends Record<string, unknown>>(properties: T): T {
  const result: Record<string, unknown> = { ...properties };
  URL_PROPERTIES.forEach((key) => {
    const value = result[key];
    if (typeof value === "string") {
      result[key] = value.replace(SECRET_PATH_SEGMENT, "/$1/[redacted]");
    }
  });
  return result as T;
}

export const redactSecretUrls = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return event;
  return {
    ...event,
    ...(event.properties ? { properties: redactUrlProperties(event.properties) } : {}),
    ...(event.$set ? { $set: redactUrlProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: redactUrlProperties(event.$set_once) } : {})
  };
};

/**
 * Initialize the SDK once, before the first render. Returns the client to hand to
 * PostHogProvider, or undefined when no key is configured.
 */
export function initPostHog(): PostHog | undefined {
  if (!posthogApiKey) return undefined;

  posthog.init(posthogApiKey, {
    api_host: posthogHost,
    defaults: "2026-06-25",
    // The SDK captures the initial pageview and every history change, so no component
    // captures $pageview itself. $pageleave follows capture_pageview by default.
    capture_pageview: "history_change",
    // Only elements carrying our conventional attributes autocapture —
    // see docs/data-attributes-convention.md.
    autocapture: {
      css_selector_allowlist: ["[data-attr]", "[data-testid]", "[data-custom-id]"]
    },
    // window.onerror and unhandledrejection become $exception (PostHog error tracking).
    // React render errors never reach window.onerror — ErrorBoundary reports those.
    capture_exceptions: true,
    capture_performance: { web_vitals: true },
    internal_or_test_user_hostname: INTERNAL_OR_TEST_HOSTNAME,
    before_send: redactSecretUrls,
    debug: true // process.env.NODE_ENV === "development"
  });

  return posthog;
}

export type PostHogClient =
  | { capture: (event: string, properties?: Record<string, unknown>) => void }
  | undefined;

export function capture(
  client: PostHogClient,
  eventName: string,
  properties?: Record<string, unknown>
) {
  if (!posthogEnabled) {
    if (process.env.NODE_ENV === "development") {
      console.log("[PostHog capture]", eventName, properties ?? {});
    }
    return;
  }
  // Call sites pass usePostHog(); fall back to the initialized singleton for the rest.
  if (client) {
    client.capture(eventName, properties);
    return;
  }
  posthog.capture(eventName, properties);
}
